import { useState } from "react";
import { Button } from "flowbite-react";
import { PurchasePointsAccessState } from "@/features/purchasePoints/components/PurchasePointsAccessState";
import {
  BACK_TO_FIRST_PAGE_LABEL,
  RETRY_DELAY_NOTICE,
} from "@/features/purchasePoints/config/purchasePoints.config";
import { isAccessCode, isBusinessCode } from "@/features/purchasePoints/errors/purchasePointsError";
import type {
  PurchasePointsError,
  PurchasePointsTechnicalCode,
} from "@/features/purchasePoints/errors/purchasePointsError";
import { useRetryDelay } from "@/features/purchasePoints/hooks/useRetryDelay";
import { ErrorMessage } from "@/shared/components/error/ErrorMessage";

type PurchasePointsErrorStateProps = {
  error: PurchasePointsError
  onRetry: () => void
  // Supplied past the first page only: there the error replaces the grid and
  // the pagination, otherwise leaving no way out.
  onBackToFirstPage?: () => void
  // Two of the technical messages below name the offers. A caller reading
  // something else — the counters, the gains — says so in its own words rather
  // than blame a catalogue it never queried.
  fallbackMessage?: string
};

// Exhaustive by construction: adding a technical code without its message does
// not compile, instead of silently falling back to "unknown".
const TECHNICAL_MESSAGES: Record<PurchasePointsTechnicalCode, string> = {
  provider_unavailable: "Le service est temporairement indisponible.",
  provider_error: "Le chargement des offres a échoué.",
  offer_not_found: "Cette offre n'est plus disponible.",
  brand_not_found: "Cette enseigne n'est pas disponible.",
  conversion_interrupted: "La conversion n'a pas abouti, vos yeaties sont intacts.",
  conversion_impossible: "Vos yeaties ne peuvent pas être convertis pour le moment.",
  unknown: "Les offres n'ont pas pu être chargées.",
};

// Firing the same request again will not bring back an offer or a brand the
// server no longer serves, nor unblock a conversion the server refuses on its
// own data: going back is the only useful way out.
const NON_RETRYABLE_CODES = ["offer_not_found", "brand_not_found", "conversion_impossible"];

export const PurchasePointsErrorState = ({
  error,
  onRetry,
  onBackToFirstPage,
  fallbackMessage,
}: PurchasePointsErrorStateProps) => {
  // Counts retries so that two failures carrying the same delay do rearm the
  // wait, instead of leaving it open from the second one on.
  const [retryAttempt, setRetryAttempt] = useState(0);
  // The provider may ask for a delay before any retry; it is honoured by
  // holding the button back rather than firing again automatically.
  const isRetryAllowed = useRetryDelay(error.retryAfterSeconds, retryAttempt);

  const handleRetry = () => {
    setRetryAttempt((previousAttempt) => previousAttempt + 1);
    onRetry();
  };

  if (isAccessCode(error.code)) return <PurchasePointsAccessState code={error.code} detail={error.detail} />;

  // A business refusal is handled where it happens, with a refreshed balance
  // and a plain message: rendering it here would dress an out-of-date screen up
  // as a failure, and offer a retry that would refuse all the same.
  if (isBusinessCode(error.code)) return null;

  const isRetryable = !NON_RETRYABLE_CODES.includes(error.code);
  const isWaitingOnProvider = isRetryable && !isRetryAllowed;

  return (
    <div data-testid={`purchase-points-error-state-${error.code}`}>
      <ErrorMessage
        message={error.detail ?? fallbackMessage ?? TECHNICAL_MESSAGES[error.code]}
        onRetry={isRetryable && isRetryAllowed ? handleRetry : undefined}
      />
      {/* A button merely removed is indistinguishable from a final failure. */}
      {isWaitingOnProvider && (
        <p
          role="status"
          className="text-gray-600 dark:text-gray-300 pb-4 text-center text-sm"
          data-testid="purchase-points-retry-delay-notice"
        >
          {RETRY_DELAY_NOTICE}
        </p>
      )}
      {onBackToFirstPage && (
        <div className="flex justify-center pb-4">
          <Button color="light" size="sm" onClick={onBackToFirstPage}>
            {BACK_TO_FIRST_PAGE_LABEL}
          </Button>
        </div>
      )}
    </div>
  );
};

PurchasePointsErrorState.displayName = "PurchasePointsErrorState";
